import { Injectable } from '@angular/core';

import { WebSocketService } from './websocket.service';
import { CallDataModel, CallDataType } from './ivr-emulator.model';
import { take } from 'rxjs';
import { MessageType } from '@sestek/common/components';
import { MessageType as CaiMessageType } from '../../chat/chat.model';
import { guid } from '@utils';

@Injectable()
export class IvrEmulatorService {
  onCallEstablished: () => void;
  onReceiveAudioMessage: (message: ArrayBuffer) => void;
  onReceiveTextMessage: (id: string, messageType: MessageType, text: string) => void;
  onReceiveTypingMessage: () => void;

  constructor(private webSocketService: WebSocketService) {
    this.webSocketService.message$.subscribe(async (message) =>
      this.handleIncomingMessage(message),
    );
  }

  startCall = async (
    callId: string,
    startCallArgs: string,
    onCallEstablished: () => void,
    onReceiveAudioMessage: (message: ArrayBuffer) => void,
    onReceiveTextMessage: (id: string, messageType: MessageType, text: string) => void,
    onReceiveTypingMessage: () => void,
  ) => {
    return await this.webSocketService.connect(callId).then(() => {
      console.log('WebSocket connected');

      this.onCallEstablished = onCallEstablished;
      this.onReceiveAudioMessage = onReceiveAudioMessage;
      this.onReceiveTextMessage = onReceiveTextMessage;
      this.onReceiveTypingMessage = onReceiveTypingMessage;

      // Wait until connected before sending the start call message
      this.webSocketService.isConnected$.pipe(take(1)).subscribe((isConnected) => {
        if (isConnected) {
          // Start the call by sending the start call arguments
          this.webSocketService.sendTextMessage(startCallArgs);
        }
      });
    });
  };

  endCall = async (endCallArgs: string): Promise<void> => {
    this.webSocketService.sendTextMessage(endCallArgs);
    await this.webSocketService.disconnect();

    this.onCallEstablished = null;
    this.onReceiveAudioMessage = null;
    this.onReceiveTextMessage = null;
    this.onReceiveTypingMessage = null;
  };

  sendAudioData = async (data: ArrayBuffer) => {
    this.webSocketService.sendAudioData(data);
  };

  private async handleIncomingMessage(incomingData: Blob | string) {
    if (incomingData instanceof Blob) {
      if (this.onReceiveAudioMessage) {
        this.onReceiveAudioMessage(await incomingData.arrayBuffer());
      }
    } else if (typeof incomingData === 'string') {
      const ivrMessageData: CallDataModel = JSON.parse(incomingData);

      if (ivrMessageData.type === CallDataType.callEstablished) {
        if (this.onCallEstablished) {
          this.onCallEstablished();
        }
      }

      if (
        ivrMessageData.type === CallDataType.message ||
        ivrMessageData.type === CallDataType.speechRecognized
      ) {
        if (this.onReceiveTextMessage) {
          this.onReceiveTextMessage(
            ivrMessageData.id ?? guid(),
            ivrMessageData.messageType === CaiMessageType.utterance
              ? MessageType.outgoing
              : MessageType.incoming,
            ivrMessageData.text,
          );
        }
      }

      if (ivrMessageData.type === CallDataType.typing) {
        if (this.onReceiveTypingMessage) {
          this.onReceiveTypingMessage();
        }
      }
    }
  }
}
